/**
 * ProductDetailPanel — read-first product detail rendered in the right drawer.
 * "Edit" flips into the same ProductForm used by Add.
 */
import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { productDisplayPrice } from '@/inventory/productDisplayPrice';
import { useEffect, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Pencil, Trash2, ArrowUpDown, AlertTriangle } from 'lucide-react';
import { database } from '@/db';
import { Product, Category, StockMovement } from '@/db/models/Inventory';
import { useObservable } from '@/hooks/useObservable';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/utils/format';
import { deleteProduct } from '@/inventory/InventoryCrudService';
import { productStockDelta } from '@/sync/liveStock';
import { ProductForm } from './ProductForm';

export function ProductDetailPanel({
  productId,
  merchantId,
  categories,
  onClose,
  onAdjust,
}: {
  productId: string;
  merchantId: string;
  categories: Category[];
  onClose: () => void;
  onAdjust: (p: Product) => void;
}) {
  const [product, setProduct] = useState<Product | null>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    const sub = database.get<Product>('products')
      .findAndObserve(productId)
      .subscribe(setProduct);
    return () => sub.unsubscribe();
  }, [productId]);

  const movements = useObservable<StockMovement[]>(
    () => database.get<StockMovement>('stock_movements').query(
      Q.where('product_id', productId),
      Q.where('is_deleted', false),
      Q.sortBy('created_at', Q.desc),
      Q.take(5),
    ).observe(),
    [productId],
    [],
  );

  if (!product) {
    return <div className="px-5 py-6 text-body text-fg-secondary">Loading…</div>;
  }

  const category = product.categoryId ? categories.find((c) => c.id === product.categoryId) : null;

  if (editing) {
    return (
      <ProductForm
        storeId={product.storeId}
        merchantId={merchantId}
        categories={categories}
        product={product}
        onSaved={() => setEditing(false)}
        onCancel={() => setEditing(false)}
      />
    );
  }

  const isOOS = product.trackInventory && !product.isService && product.currentStock <= 0;
  const isLow = product.trackInventory && !product.isService && product.currentStock > 0 && product.currentStock <= product.lowStockAlert;

  const margin = product.purchasePrice > 0 ? product.sellingPrice - product.purchasePrice : null;
  const marginPct = margin !== null && product.sellingPrice > 0 ? (margin / product.sellingPrice) * 100 : null;
  const mrpDiscountPct = product.mrp > product.sellingPrice && product.mrp > 0
    ? ((product.mrp - product.sellingPrice) / product.mrp) * 100
    : null;
  const stockValue = product.trackInventory && !product.isService && product.purchasePrice > 0
    ? product.currentStock * product.purchasePrice
    : null;
  const stockStatus = isOOS ? 'Out of stock' : isLow ? 'Low stock' : 'In stock';
  const stockStatusTone = isOOS ? 'danger' : isLow ? 'warning' : 'success';

  async function handleDelete() {
    if (!product) return;
    setError(null);
    try {
      await deleteProduct(product);
      notifySuccess(`“${product.name}” deleted.`);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to delete.');
      setConfirmingDelete(false);
    }
  }

  return (
    <div className="flex max-h-[min(84vh,680px)] flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
        {/* Hero */}
        <div className="space-y-2">
          <div className="text-heading text-fg-primary sm:text-display">{product.name}</div>
          <div className="flex items-center gap-2 text-label uppercase text-fg-tertiary">
            {category && <span>{category.name}</span>}
            {product.isService && <Tag color="accent">Service</Tag>}
            {!product.isActive && <Tag color="danger">Inactive</Tag>}
          </div>
          {!product.isService && product.trackInventory && (
            <div className="flex items-center gap-2">
              <Pill tone={stockStatusTone}>{stockStatus}</Pill>
              <Pill tone="muted">Stock {product.currentStock} {product.unit}</Pill>
              {margin !== null && <Pill tone={margin >= 0 ? 'success' : 'danger'}>Margin {formatCurrency(margin)}</Pill>}
            </div>
          )}
        </div>

        {/* Pricing grid */}
        <Section title="Pricing">
          <Cell label="Selling price" emphasis>
            {formatCurrency(productDisplayPrice(product))}
            {mrpDiscountPct !== null && (
              <span className="ml-2 text-label text-success-ink">{mrpDiscountPct.toFixed(0)}% off MRP</span>
            )}
          </Cell>
          <Cell label="MRP">{formatCurrency(product.mrp)}</Cell>
          {product.purchasePrice > 0 && <Cell label="Purchase price">{formatCurrency(product.purchasePrice)}</Cell>}
          <Cell label="GST rate">{product.gstRate}%</Cell>
          {margin !== null && (
            <Cell label="Margin / unit">
              <span className={margin >= 0 ? 'text-success-ink' : 'text-danger-ink'}>
                {formatCurrency(margin)}
                {marginPct !== null && <span className="ml-1 text-label">({marginPct.toFixed(1)}%)</span>}
              </span>
            </Cell>
          )}
        </Section>

        {/* Stock */}
        {product.trackInventory && !product.isService && (
          <Section title="Stock">
            <div className="col-span-2 flex items-baseline gap-3">
              <span className={'text-heading tabular-nums sm:text-display ' + (isOOS ? 'text-danger-ink' : isLow ? 'text-warning-ink' : 'text-fg-primary')}>
                {product.currentStock}
              </span>
              <span className="text-body text-fg-secondary">{product.unit}</span>
              {(isOOS || isLow) && (
                <span className="ml-auto inline-flex items-center gap-1 text-label uppercase text-warning-ink">
                  <AlertTriangle size={12} /> {isOOS ? 'Out of stock' : 'Low'}
                </span>
              )}
            </div>
            {product.lowStockAlert > 0 && (
              <Cell label="Low-stock alert">{product.lowStockAlert} {product.unit}</Cell>
            )}
            {stockValue !== null && (
              <Cell label="Stock value (at cost)">{formatCurrency(stockValue)}</Cell>
            )}
          </Section>
        )}

        {/* Identifiers */}
        <Section title="Identifiers">
          <Cell label="SKU">{product.sku || '—'}</Cell>
          <Cell label="Barcode">{product.barcode || '—'}</Cell>
          <Cell label="HSN / SAC">{product.hsnCode || '—'}</Cell>
          <Cell label="Unit">{product.unit}</Cell>
        </Section>

        {/* Recent stock movements */}
        {product.trackInventory && !product.isService && movements.length > 0 && (
          <div>
            <div className="mb-2 text-label uppercase text-fg-tertiary">Recent stock activity</div>
            <div className="divide-y divide-stroke-hairline/5 rounded-md border border-stroke-hairline/10 bg-bg-canvas px-3">
              {movements.map((m) => (
                <MovementRow key={m.id} movement={m} unit={product.unit} />
              ))}
            </div>
          </div>
        )}
        {product.trackInventory && !product.isService && movements.length === 0 && (
          <div className="rounded-md border border-stroke-hairline/10 bg-bg-canvas px-3 py-2 text-body text-fg-secondary">
            No stock activity yet for this item.
          </div>
        )}

        {/* Sync state — useful for debugging during Phase 3/4 */}
        <div className="text-label uppercase text-fg-tertiary">
          Updated {product.updatedAt.toLocaleString()} · v{product.version} · {product.isSynced ? 'synced' : 'pending'}
        </div>

      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between gap-2 border-t border-stroke-hairline/10 px-4 py-2.5">
        <Button variant="ghost" onClick={() => setConfirmingDelete((v) => !v)}>
          <Trash2 size={14} /> {confirmingDelete ? 'Confirm delete' : 'Delete'}
        </Button>
        {confirmingDelete ? (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete}>Delete permanently</Button>
          </div>
        ) : (
          <div className="flex gap-2">
            {product.trackInventory && !product.isService && (
              <Button variant="secondary" onClick={() => onAdjust(product)}>
                <ArrowUpDown size={14} /> Adjust stock
              </Button>
            )}
            <Button onClick={() => setEditing(true)}>
              <Pencil size={14} /> Edit
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
const MOVEMENT_LABELS: Record<string, string> = {
  ADJUSTMENT: 'Adjustment',
  SALE: 'Sale',
  PURCHASE: 'Purchase',
  RETURN: 'Return',
  TRANSFER_IN: 'Transfer in',
  TRANSFER_OUT: 'Transfer out',
  IMPORT: 'Import',
};

function MovementRow({ movement, unit }: { movement: StockMovement; unit: string }) {
  // Direction must come from the stock effect, not the raw quantity sign: a sale
  // is stored as type OUT with a *positive* quantity, so `quantity > 0` would
  // mislabel it as IN. `productStockDelta` is the canonical signed delta (the
  // same one that derives current_stock).
  const delta = productStockDelta(movement.type, movement.quantity);
  const label = MOVEMENT_LABELS[movement.type] ?? movement.type;
  const dirLabel = delta > 0 ? 'IN' : delta < 0 ? 'OUT' : '—';
  const amountClass = delta > 0 ? 'text-success-ink' : delta < 0 ? 'text-danger-ink' : 'text-fg-tertiary';
  return (
    <div className="flex items-center justify-between gap-2.5 py-1.5">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-body text-fg-primary">
          <span>{label}</span>
          <Pill tone="muted">{dirLabel}</Pill>
        </div>
        {movement.notes && <div className="truncate text-label text-fg-secondary">{movement.notes}</div>}
        <div className="text-label text-fg-tertiary">{movement.createdAt.toLocaleString()}</div>
      </div>
      <span className={'shrink-0 tabular-nums text-body font-medium ' + amountClass}>
        {delta > 0 ? '+' : ''}{delta} {unit}
      </span>
    </div>
  );
}
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-label uppercase text-fg-tertiary">{title}</div>
      <div className="grid grid-cols-2 gap-2.5 rounded-md border border-stroke-hairline/10 bg-bg-canvas p-2.5">
        {children}
      </div>
    </div>
  );
}

function Cell({ label, children, emphasis }: { label: string; children: React.ReactNode; emphasis?: boolean }) {
  return (
    <div>
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={'mt-0.5 ' + (emphasis ? 'text-heading text-fg-primary tabular-nums' : 'text-body text-fg-primary')}>
        {children}
      </div>
    </div>
  );
}

function Tag({ children, color }: { children: React.ReactNode; color: 'accent' | 'danger' }) {
  const cls = color === 'accent'
    ? 'border-accent/30 bg-accent/10 text-accent-ink'
    : 'border-danger/30 bg-danger/10 text-danger-ink';
  return (
    <span className={'rounded px-1.5 py-0.5 text-label uppercase border ' + cls}>{children}</span>
  );
}

function Pill({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'success' | 'warning' | 'danger' | 'muted';
}) {
  const cls = tone === 'success'
    ? 'border-success/30 bg-success/10 text-success-ink'
    : tone === 'warning'
      ? 'border-warning/30 bg-warning/10 text-warning-ink'
      : tone === 'danger'
        ? 'border-danger/30 bg-danger/10 text-danger-ink'
        : 'border-stroke-hairline/20 bg-bg-surface text-fg-secondary';
  return <span className={'rounded-full border px-1.5 py-0.5 text-label ' + cls}>{children}</span>;
}
